import React from "react";
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { all_routes } from "../../Router/all_routes";
import { useSelector } from "react-redux";
import apiClient from "../../api/apiClient";
import { toast } from "sonner";
import { currencySettingsData } from "../../core/json/currencySettings";
import { API_BASE_URL } from "../../api/header";
import timezones from "timezones.json";
import { languageSettingsData } from "../../core/json/languageSettings";
import DefaultEditor from "react-simple-wysiwyg";
import {
  ShoppingCart,
  Save,
  X,
  Info,
  MapPin,
  Globe,
  Percent,
  DollarSign,
  Shield,
  Monitor,
  Printer,
  Image as ImageIcon,
  Loader2,
} from "lucide-react";
import MediaLibraryComponent from "../media/MediaLibraryComponent";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ReactSelect } from "@/components/ui/react-select";
import { Country, State } from "country-state-city";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { PageContainer } from "@/components/layout/PageContainer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { AddressAutocomplete } from "@/components/ui/address-autocomplete";
import RecordNotFound from "@/core/components/RecordNotFound";

const taxClassOptions = [
  { value: "GST", label: "GST" },
  { value: "VAT", label: "VAT" },
  { value: "NA", label: "None" },
  { value: "Other", label: "Other" },
];

const AddStore = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [store, setStore] = useState({
    name: "",
    alternate_name: "",
    logo: "",
    description: "",
    email: "",
    phone: "",
    mobile: "",
    fax: "",
    website: "",
    address: "",
    city: "",
    postcode: "",
    state: "",
    country: "",
    zip: "",
    time_zone: "",
    time_format: "",
    language: "",
    default_currency: "",
    price_format: "",
    decimal_format: "",
    default_tax_class: "NA",
    tax_percentage: 0,
    registration_no: "",
    start_time: "",
    end_time: "",
    default_address: "",
    api_key: "",
    accounting_method: "",
    send_email_from: "",
    is_send_email_from_verified: false,
    receive_all_emails: false,
    require_two_factor_auth: false,
    charge_restocking_fee: false,
    restocking_fee_percentage: 0,
    charge_deposit: false,
    deposit_amount: 0,
    charge_tax_on_deposit: false,
    deposit_tax_class: "",
    default_charge_deposit: false,
    lock_screen_enabled: false,
    screen_timeout_minutes: 1,
    terms_and_conditions: "",
    status: "active",
    print_agent_enabled: false,
    print_agent_pairing_key: "",
  });

  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };

  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);

  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageDetails, setSelectedImageDetails] = useState(null);

  // Mutations
  const createStoreMutation = useMutation({
    mutationFn: async (newStore) => {
      const response = await apiClient.post(`${API_BASE_URL}/store`, newStore);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stores"] });
      toast.success("Store added successfully!");
      setTimeout(() => {
        navigate(all_routes.storeList);
      }, 1500);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to add store");
    },
  });

  const fetchMediaDetails = async (mediaId) => {
    try {
      const res = await apiClient.get(`${API_BASE_URL}/media/${mediaId}`);
      if (res.status === 200) {
        setSelectedImageDetails(res.data.data);
        return res.data.data;
      }
    } catch (error) {
      console.log("Failed to fetch media details", error);
      return null;
    }
  };

  const handleMediaSelect = async (selectedId) => {
    setSelectedImage(selectedId);
    const data = await fetchMediaDetails(selectedId);
    if (data) {
      setStore({
        ...store,
        logo: data.url || "",
      });
    }
  };

  const validateFields = () => {
    if (store.name === "") {
      toast.error("Store name is required!");
      return false;
    }
    if (
      !store.default_currency ||
      store.default_currency === "" ||
      (typeof store.default_currency === "object" &&
        !store.default_currency?.id)
    ) {
      toast.error("Default currency is required!");
      return false;
    }
    return true;
  };

  const countries = useMemo(
    () =>
      Country.getAllCountries().map((c) => ({
        value: c.isoCode,
        label: c.name,
      })),
    [],
  );

  const selectedCountryCode = useMemo(() => {
    const c = Country.getAllCountries().find((c) => c.name === store.country);
    return c ? c.isoCode : "";
  }, [store.country]);

  const stateOptions = useMemo(
    () =>
      selectedCountryCode
        ? State.getStatesOfCountry(selectedCountryCode).map((s) => ({
          value: s.name,
          label: s.name,
        }))
        : [],
    [selectedCountryCode],
  );

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let finalValue = type === "checkbox" ? checked : value;

    // Handle numeric fields
    if (
      [
        "tax_percentage",
        "restocking_fee_percentage",
        "deposit_amount",
        "screen_timeout_minutes",
      ].includes(name)
    ) {
      finalValue = value === "" ? "" : parseFloat(value);
    }

    setStore({
      ...store,
      [name]: finalValue,
    });
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validateFields()) {
      return;
    }
    const payload = {
      ...store,
      start_time: store.start_time || null,
      end_time: store.end_time || null,
    };
    createStoreMutation.mutate(payload);
  };

  const handleNavigation = (route) => {
    navigate(route);
  };

  const isLoading = createStoreMutation.isPending;

  if (!hasPermission("store create")) {
    return <RecordNotFound />;
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Add New Store"
          subtitle="Create store profile and configuration"
        />
        <PageHeaderActions>
          <Button
            variant="outline"
            onClick={() => handleNavigation(all_routes.storeList)}
          >
            <X className="mr-2 h-4 w-4" />
            Cancel
          </Button>
        </PageHeaderActions>
      </PageHeader>

      <form onSubmit={handleSubmit} className="space-y-6 pb-12">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-primary" />
              Basic Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">
                  Store Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={store.name || ""}
                  onChange={handleChange}
                  placeholder="Enter store name"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="alternate_name">Alternate Name</Label>
                <Input
                  id="alternate_name"
                  name="alternate_name"
                  value={store.alternate_name || ""}
                  onChange={handleChange}
                  placeholder="Enter alternate name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="registration_no">Registration Number</Label>
                <Input
                  id="registration_no"
                  name="registration_no"
                  value={store.registration_no || ""}
                  onChange={handleChange}
                  placeholder="Business registration number"
                />
              </div>

              <div className="md:col-span-3 space-y-2">
                <Label>Logo</Label>
                <div className="flex items-center gap-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsMediaLibraryOpen(true)}
                  >
                    <ImageIcon className="mr-2 h-4 w-4" />
                    {store.logo ? "Change Logo" : "Select Logo"}
                  </Button>
                  {(selectedImageDetails || store.logo) && (
                    <div className="flex items-center gap-2">
                      <div className="relative h-20 w-20 border rounded-lg overflow-hidden bg-muted">
                        <img
                          src={selectedImageDetails?.url || store.logo}
                          alt="Store Logo"
                          className="h-full w-full object-contain"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => {
                          setSelectedImageDetails(null);
                          setStore((prev) => ({ ...prev, logo: "" }));
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Store Email</Label>
                <Input
                  id="email"
                  type="email"
                  name="email"
                  value={store.email || ""}
                  onChange={handleChange}
                  placeholder="store@example.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={store.phone || ""}
                  onChange={handleChange}
                  placeholder="Phone number"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="mobile">Mobile</Label>
                <Input
                  id="mobile"
                  name="mobile"
                  value={store.mobile || ""}
                  onChange={handleChange}
                  placeholder="Mobile number"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <ReactSelect
                  options={[
                    { value: "active", label: "Active" },
                    { value: "inactive", label: "Inactive" },
                  ]}
                  value={{
                    value: store.status,
                    label:
                      store.status.charAt(0).toUpperCase() +
                      store.status.slice(1),
                  }}
                  onChange={(opt) =>
                    setStore({ ...store, status: opt ? opt.value : "active" })
                  }
                />
              </div>

              <div className="md:col-span-1 space-y-2">
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  type="url"
                  name="website"
                  value={store.website || ""}
                  onChange={handleChange}
                  placeholder="https://example.com"
                />
              </div>

              <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Description</Label>
                  <DefaultEditor
                    value={store.description || ""}
                    onChange={(e) =>
                      setStore({ ...store, description: e.target.value })
                    }
                    className="min-h-[150px] border rounded-md"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Terms and Conditions</Label>
                  <DefaultEditor
                    value={store.terms_and_conditions || ""}
                    onChange={(e) =>
                      setStore({
                        ...store,
                        terms_and_conditions: e.target.value,
                      })
                    }
                    className="min-h-[150px] border rounded-md"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Address Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-primary" />
              Address Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-4 space-y-2">
                <Label htmlFor="address">Address</Label>
                <AddressAutocomplete
                  id="address"
                  name="address"
                  value={store.address || ""}
                  onChange={(val) =>
                    setStore((prev) => ({ ...prev, address: val }))
                  }
                  onPlaceSelect={({
                    address,
                    city,
                    state,
                    country,
                    postcode,
                  }) =>
                    setStore((prev) => ({
                      ...prev,
                      address: address || prev.address,
                      city: city || prev.city,
                      state: state || prev.state,
                      country: country || prev.country,
                      postcode: postcode || prev.postcode,
                    }))
                  }
                  placeholder="Search for address…"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  name="city"
                  value={store.city || ""}
                  onChange={handleChange}
                  placeholder="City"
                />
              </div>
              <div className="space-y-2">
                <Label>Country</Label>
                <ReactSelect
                  options={countries}
                  value={
                    countries.find((c) => c.label === store.country) || null
                  }
                  onChange={(opt) =>
                    setStore({
                      ...store,
                      country: opt ? opt.label : "",
                      state: "",
                    })
                  }
                  placeholder="Select country"
                  isClearable
                />
              </div>
              <div className="space-y-2">
                <Label>State/Province</Label>
                <ReactSelect
                  options={stateOptions}
                  value={
                    stateOptions.find((s) => s.value === store.state) || null
                  }
                  onChange={(opt) =>
                    setStore({ ...store, state: opt ? opt.value : "" })
                  }
                  placeholder={
                    selectedCountryCode
                      ? "Select state"
                      : "Select country first"
                  }
                  isDisabled={!selectedCountryCode}
                  isClearable
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="postcode">Postal Code</Label>
                <Input
                  id="postcode"
                  name="postcode"
                  value={store.postcode || ""}
                  onChange={handleChange}
                  placeholder="Postal Code"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Regional Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-primary" />
              Regional Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Time Zone</Label>
                <ReactSelect
                  options={timezones.map((tz) => ({
                    value: `${tz.value}-${tz.abbr}`,
                    label: tz.text,
                  }))}
                  value={
                    store.time_zone
                      ? {
                        value: store.time_zone,
                        label: timezones.find(
                          (tz) =>
                            `${tz.value}-${tz.abbr}` === store.time_zone,
                        )?.text,
                      }
                      : null
                  }
                  onChange={(opt) =>
                    setStore({ ...store, time_zone: opt ? opt.value : "" })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Time Format</Label>
                <ReactSelect
                  options={[
                    { value: "12h", label: "12-hour (AM/PM)" },
                    { value: "24h", label: "24-hour" },
                  ]}
                  value={
                    store.time_format
                      ? {
                        value: store.time_format,
                        label:
                          store.time_format === "12h" ? "12-hour" : "24-hour",
                      }
                      : null
                  }
                  onChange={(opt) =>
                    setStore({ ...store, time_format: opt ? opt.value : "" })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Language</Label>
                <ReactSelect
                  options={languageSettingsData.map((lang) => ({
                    value: lang.code,
                    label: lang.language,
                  }))}
                  value={
                    languageSettingsData
                      .map((l) => ({ value: l.code, label: l.language }))
                      .find((opt) => opt.value === store.language) || null
                  }
                  onChange={(opt) =>
                    setStore({ ...store, language: opt?.value || "" })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>
                  Default Currency <span className="text-destructive">*</span>
                </Label>
                <ReactSelect
                  options={currencySettingsData.map((c) => ({
                    value: String(c.id),
                    label: `${c.name} (${c.symbol})`,
                    currency: c,
                  }))}
                  value={
                    currencySettingsData
                      .map((c) => ({
                        value: String(c.id),
                        label: `${c.name} (${c.symbol})`,
                        currency: c,
                      }))
                      .find(
                        (opt) =>
                          opt.value ===
                          String(
                            typeof store.default_currency === "object"
                              ? store.default_currency?.id
                              : store.default_currency,
                          ),
                      ) || null
                  }
                  onChange={(opt) =>
                    setStore({
                      ...store,
                      default_currency: opt?.currency || "",
                    })
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Store Hours</Label>
                <div className="flex gap-2">
                  <Input
                    type="time"
                    name="start_time"
                    value={store.start_time || ""}
                    onChange={handleChange}
                  />
                  <Input
                    type="time"
                    name="end_time"
                    value={store.end_time || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tax & Sales Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Percent className="h-5 w-5 text-primary" />
              Tax & Sales Settings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Default Tax Class</Label>
                <ReactSelect
                  options={taxClassOptions}
                  value={
                    taxClassOptions.find(
                      (opt) => opt.value === store.default_tax_class,
                    ) ||
                    (store.default_tax_class
                      ? taxClassOptions.find((opt) => opt.value === "Other")
                      : null)
                  }
                  onChange={(opt) =>
                    setStore({
                      ...store,
                      default_tax_class: opt?.value || "",
                      tax_percentage:
                        opt?.value === "NA" ? 0 : store.tax_percentage,
                    })
                  }
                />
                {(!["GST", "VAT", "NA", ""].includes(store.default_tax_class) ||
                  store.default_tax_class === "Other") && (
                    <Input
                      className="mt-2"
                      placeholder="Enter custom tax class"
                      value={
                        store.default_tax_class === "Other"
                          ? ""
                          : store.default_tax_class
                      }
                      onChange={(e) =>
                        setStore({
                          ...store,
                          default_tax_class: e.target.value,
                        })
                      }
                    />
                  )}
              </div>
              <div className="space-y-2">
                <Label>Tax Percentage (%)</Label>
                <Input
                  type="number"
                  name="tax_percentage"
                  value={store.tax_percentage || ""}
                  onChange={handleChange}
                  min="0"
                  max="100"
                  disabled={store.default_tax_class === "NA"}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Refund & Deposit Settings */}
        <Card style={{ display: 'none' }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-primary" />
              Refund & Deposit Settings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Charge Restocking Fee</Label>
                  <Switch
                    checked={store.charge_restocking_fee}
                    onCheckedChange={(checked) =>
                      setStore({ ...store, charge_restocking_fee: checked })
                    }
                  />
                </div>
                {store.charge_restocking_fee && (
                  <div className="space-y-2 animate-in slide-in-from-top-2">
                    <Label>Restocking Fee (%)</Label>
                    <Input
                      type="number"
                      name="restocking_fee_percentage"
                      value={store.restocking_fee_percentage || ""}
                      onChange={handleChange}
                      min="0"
                      max="100"
                    />
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Charge Deposit</Label>
                  <Switch
                    checked={store.charge_deposit}
                    onCheckedChange={(checked) =>
                      setStore({ ...store, charge_deposit: checked })
                    }
                  />
                </div>
                {store.charge_deposit && (
                  <div className="grid grid-cols-2 gap-4 animate-in slide-in-from-top-2">
                    <div className="space-y-2">
                      <Label>Amount</Label>
                      <Input
                        type="number"
                        name="deposit_amount"
                        value={store.deposit_amount || ""}
                        onChange={handleChange}
                        min="0"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Deposit Tax Class</Label>
                      <ReactSelect
                        options={taxClassOptions}
                        value={
                          taxClassOptions.find(
                            (opt) => opt.value === store.deposit_tax_class,
                          ) || null
                        }
                        onChange={(opt) =>
                          setStore({
                            ...store,
                            deposit_tax_class: opt?.value || "",
                          })
                        }
                        placeholder="Select tax class"
                      />
                    </div>
                    <div className="col-span-2 flex items-center justify-between">
                      <Label>Tax on Deposit</Label>
                      <Switch
                        checked={store.charge_tax_on_deposit}
                        onCheckedChange={(checked) =>
                          setStore({ ...store, charge_tax_on_deposit: checked })
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Security & Screen */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" style={{ display: 'none' }}>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                Security Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
                <div className="space-y-0.5">
                  <Label>Two-Factor Auth</Label>
                  <p className="text-xs text-muted-foreground">
                    Mandatory 2FA for users
                  </p>
                </div>
                <Switch
                  checked={store.require_two_factor_auth}
                  onCheckedChange={(checked) =>
                    setStore({ ...store, require_two_factor_auth: checked })
                  }
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Monitor className="h-5 w-5 text-primary" />
                Screen Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Lock Screen</Label>
                  <p className="text-xs text-muted-foreground">
                    Auto-lock on inactivity
                  </p>
                </div>
                <Switch
                  checked={store.lock_screen_enabled}
                  onCheckedChange={(checked) =>
                    setStore({ ...store, lock_screen_enabled: checked })
                  }
                />
              </div>
              {store.lock_screen_enabled && (
                <div className="space-y-2 animate-in slide-in-from-top-2">
                  <Label>Timeout (Minutes)</Label>
                  <Input
                    type="number"
                    name="screen_timeout_minutes"
                    value={store.screen_timeout_minutes || ""}
                    onChange={handleChange}
                    min="1"
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Print Agent Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Printer className="h-5 w-5 text-primary" />
              Print Agent Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/30">
              <div className="space-y-0.5">
                <Label>Enable Print Agent</Label>
                <p className="text-xs text-muted-foreground">
                  Allow this store to use the print agent service
                </p>
              </div>
              <Switch
                checked={!!store.print_agent_enabled}
                onCheckedChange={(checked) =>
                  setStore({ ...store, print_agent_enabled: checked })
                }
              />
            </div>
            {store.print_agent_enabled && (
              <div className="space-y-2 animate-in slide-in-from-top-2">
                <Label htmlFor="print_agent_pairing_key">Pairing Key</Label>
                <Input
                  id="print_agent_pairing_key"
                  name="print_agent_pairing_key"
                  value={store.print_agent_pairing_key || ""}
                  onChange={handleChange}
                  placeholder="Enter print agent pairing key"
                  maxLength={255}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-6 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleNavigation(all_routes.storeList)}
            className="w-32"
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading} className="w-32">
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Add Store
              </>
            )}
          </Button>
        </div>
      </form>

      <MediaLibraryComponent
        isOpen={isMediaLibraryOpen}
        onClose={() => setIsMediaLibraryOpen(false)}
        onSelect={handleMediaSelect}
        isMultiple={false}
        selectedIds={selectedImage ? [selectedImage] : []}
      />
    </PageContainer>
  );
};

export default AddStore;
